import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Stack,
  Divider,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

const Footer = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const footerSections = [
    {
      title: 'Company',
      links: [
        { label: 'About Us', action: () => {} },
        { label: 'Careers', action: () => {} },
        { label: 'Blog', action: () => {} },
        { label: 'Press', action: () => {} },
      ],
    },
    {
      title: 'Services',
      links: [
        { label: 'Find Services', action: () => navigate('/services') },
        { label: 'List Your Service', action: () => navigate('/add-listing') },
        { label: 'Vendor Login', action: () => navigate('/auth') },
        { label: 'How It Works', action: () => {} },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'Help Center', action: () => {} },
        { label: 'Contact Us', action: () => {} },
        { label: 'Privacy Policy', action: () => {} },
        { label: 'Terms & Conditions', action: () => {} },
      ],
    },
  ];

  const contactInfo = [
    { icon: LocationOnIcon, text: '123 Main Street, City, Country' },
    { icon: PhoneIcon, text: '+91 7840046570' },
    { icon: EmailIcon, text: 'support@nearbyservice.com' },
  ];

  const socialLinks = [
    { icon: FacebookIcon, label: 'Facebook', color: '#3b5998' },
    { icon: TwitterIcon, label: 'Twitter', color: '#1DA1F2' },
    { icon: InstagramIcon, label: 'Instagram', color: '#E1306C' },
    { icon: LinkedInIcon, label: 'LinkedIn', color: '#0077b5' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        color: '#ffffff',
        pt: { xs: 4, sm: 6, md: 8 },
        pb: { xs: 3, sm: 4, md: 6 },
        mt: 0,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Container maxWidth="lg">
        {/* Top Features Section */}

        {/* Main Content */}
        <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} sx={{ mb: 6 }}>
          {/* Brand */}
          <Grid item xs={12} sm={6} md={3} sx={{ animation: `slideInLeft 0.5s ease-out backwards` }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '18px', sm: '20px', md: '16px' },
                  mb: 2,
                  background: 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                NearByService
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#cbd5e1',
                  lineHeight: { xs: 1.8, md: 1.5 },
                  fontSize: { xs: '13px', sm: '14px', md: '12px' },
                  mb: 2.5,
                }}
              >
                Connecting local service providers with customers in your neighborhood.
              </Typography>

              {/* Contact Info */}
              <Stack spacing={{ xs: 1.5, md: 1 }}>
                {contactInfo.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Icon sx={{ fontSize: { xs: 18, md: 16 }, color: '#4f46e5', mt: 0.5, flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#cbd5e1',
                          fontSize: { xs: '12px', sm: '13px', md: '11px' },
                          lineHeight: 1.4,
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Grid>

          {/* Links Sections */}
          {footerSections.map((section, idx) => {
            const delays = ['0.05s', '0.1s', '0.15s'];
            return (
            <Grid item xs={12} sm={6} md={3} key={idx} sx={{ animation: `slideInUp 0.5s ease-out backwards`, animationDelay: delays[idx] }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 2.5,
                  fontSize: { xs: '14px', sm: '15px' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#f1f5f9',
                }}
              >
                {section.title}
              </Typography>
              <Stack spacing={1.2}>
                {section.links.map((link, linkIdx) => (
                  <Button
                    key={linkIdx}
                    onClick={link.action}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      color: '#cbd5e1',
                      fontSize: { xs: '13px', sm: '14px' },
                      p: 0,
                      '&:hover': {
                        color: '#4f46e5',
                        pl: 1,
                        transition: 'all 0.2s ease',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            </Grid>
            );
          })}
        </Grid>

        {/* Divider */}
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 4 }} />

        {/* Bottom Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
            marginBottom: 5
          }}
        >
          {/* Left Content */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: 3,
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Copyright */}
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontSize: { xs: '12px', sm: '13px' },
                whiteSpace: 'nowrap',
              }}
            >
              © {new Date().getFullYear()} NearByService. All rights reserved.
            </Typography>
          </Box>

          {/* Social Links */}
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {socialLinks.map((social, idx) => {
              const Icon = social.icon;
              return (
                <Button
                  key={idx}
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    borderRadius: '50%',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#cbd5e1',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#ffffff',
                      borderColor: social.color,
                      background: `${social.color}20`,
                    },
                  }}
                  aria-label={social.label}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </Button>
              );
            })}
          </Stack>

        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
